import { uniqueId } from "lodash";
import * as React from "react";
import { Link, RouteComponentProps } from "react-router-dom";

import { ActionMenuItemStandard } from "@app/shared/action-menu";
import { ArchiveProjectLink, EditProjectOptionsLink, ProjectTileData } from "../projects";
import { FilteredResource, Resource, ResourceType } from "../resources";
import { handleArchiveConfirm, handleHiddenChange, handleUpdateSummary, handleCancelQueuedProject } from "./Helpers";
import JobsPanelContentLink from "../projects/network-assessment/JobsPanelContentLink";
import { RoleRestricted, Roles } from "../user";
import { ProjectRunConfigurationStatus } from "../projects/enumerators";
import { toast, ToastOptions } from "react-toastify";
import { NewActionMenu } from "@app/shared/action-menu";
import { QESButton } from "@app/shared/qes-standards";
import { BreadcrumbTemplateEnum, breadcrumbTemplates } from "../shared/breadcrumbs/breadcrumbTemplates";
import { ZipJobsPanelContentLink } from "@app/projects/network-assessment/ZipJobsPanelContentLink";

interface ProjectOwnerOptions {
  /** Event triggered after successful archive */
  onArchived: (projectId: number) => void;
  onUpdateProjectOptions: (summary: ProjectTileData) => void;
  projectResource: Resource;
  projectTileData: ProjectTileData;
}

interface ProjectActionMenuProps extends RouteComponentProps {
  filteredProjectResource: FilteredResource;
  // required if the project is owned
  ownerOptions?: ProjectOwnerOptions;
  projectOwned: boolean;
  setProjectIsReadOnly?: (projectIds: number[]) => void;
  showingHidden?: boolean;
  onHidden: (projectId: number) => void;
  fixMenu?: boolean;
}

interface ProjectActionMenuState {
  sidePanelArchiveWarningVisible: boolean;
}

export default class ProjectActionMenu extends React.Component<ProjectActionMenuProps, ProjectActionMenuState> {
  private readonly sidePanelId = uniqueId("side-panel-");
  private dotsMenuRef = React.createRef<HTMLButtonElement>();

  constructor(props: ProjectActionMenuProps) {
    super(props);
    this.state = {
      sidePanelArchiveWarningVisible: false,
    };
  }

  public render() {
    const menu = this.props.projectOwned ? this.getOwnerMenu() : this.getViewerMenu();

    return (
      <NewActionMenu
        alignRight={true}
        buttonClassNames="p-0 text-dark btn-icon btn-sm"
        containerClassnames="project-action-menu-container"
        buttonText={<i className="fa fa-ellipsis-v" />}
        hideArrow={true}
        testId={`project-tile__menu--${this.props.filteredProjectResource.resourceId}`}
        fixMenu={this.props.fixMenu}
        fixedMenuOffset={{
          x: 8,
          y: 0,
        }}
        triggerElementOverride={this.dotsMenuRef}
      >
        {menu}
      </NewActionMenu>
    );
  }

  private getOwnerMenu = () => {
    const resource = this.props.ownerOptions && this.props.ownerOptions.projectResource;
    const archiveIcon = resource && this.props.ownerOptions.projectResource.isArchived ? "fa fa-sellsy" : "fa fa-archive";
    const archiveText = resource && this.props.ownerOptions.projectResource.isArchived ? "Unarchive" : "Archive";
    const archiveDisabled = !resource || (!this.props.ownerOptions.projectTileData || (this.props.ownerOptions.projectTileData && (this.props.ownerOptions.projectTileData.isRunning || (this.props.ownerOptions.projectTileData.isReadOnly && !this.props.ownerOptions.projectResource.isArchived))));
    const isQueued = resource && this.props.ownerOptions.projectTileData && this.props.ownerOptions.projectTileData.runConfigurationStatus === ProjectRunConfigurationStatus.Queued;
    const isZipAnalysis = resource && resource.resourceType === ResourceType.ZipAnalysisProject;

    return (
      <>
        <ArchiveProjectLink archiveDisabled={archiveDisabled} archiveText={archiveText} archiveIcon={archiveIcon} onArchiveConfirm={() => handleArchiveConfirm(resource && this.props.ownerOptions.projectResource.isArchived || null, this.props.ownerOptions.projectResource.resourceId, this.props.ownerOptions.onArchived)} />
        <Link to={`/projects/${this.props.filteredProjectResource.resourceId}/clone`} className="d-block col-12 action-menu-item side-panel-link font-weight-normal"><i className="fa fa-clone" /> Clone</Link>
        <EditProjectOptionsLink
          containerId={this.sidePanelId}
          disabled={!this.props.ownerOptions.projectTileData}
          filteredProjectResource={this.props.filteredProjectResource}
          onUpdateProjectOptions={() => handleUpdateSummary(this.props.ownerOptions.onUpdateProjectOptions, this.props.ownerOptions.projectResource, this.props.filteredProjectResource)}
          setProjectIsReadOnly={this.props.setProjectIsReadOnly}
          triggerElementOverride={this.dotsMenuRef}
        />
        <div className="col-md-12 action-menu-item">
          <Link
            data-testid={`project-action__menu--${this.props.filteredProjectResource.resourceId}-share-unshare`}
            to={{
              pathname: `/resources/share`,
              state: {
                breadcrumbItems: [
                  ...breadcrumbTemplates[BreadcrumbTemplateEnum.NetworkAssessment],
                  {
                    link: this.props.location.pathname,
                    text: this.props.ownerOptions.projectResource.name,
                  }
                ],
                isSingleResource: true,
                resourceDescription: "Project",
                resourceId: this.props.ownerOptions.projectResource.resourceId,
                resourceTypes: [ResourceType.Project]
              }
            }}
          >
            <i className="fa fa-share" /> Share/Unshare
          </Link>
        </div>
        <RoleRestricted allowedRoles={[Roles.QUEST_USER, Roles.QUEST_ADMIN]}>
          {isZipAnalysis
            ? <ZipJobsPanelContentLink projectId={resource?.resourceId} projectName={resource?.name} triggerElementOverride={this.dotsMenuRef} />
            : <JobsPanelContentLink containerId={this.sidePanelId} projectId={this.props.ownerOptions.projectResource.resourceId} projectName={this.props.ownerOptions.projectResource.name} triggerElementOverride={this.dotsMenuRef} />
          }
        </RoleRestricted>
        {isQueued && resource && (<ActionMenuItemStandard onClick={() => { this.onClickCancelProjectExecution(resource.resourceId) }} faIcon="fa-stop" displayText="Cancel Project Execution" />)}
      </>
    );
  }

  private getViewerMenu = () => (
    <>
      <EditProjectOptionsLink
        containerId={this.sidePanelId}
        disabled={true}
        filteredProjectResource={this.props.filteredProjectResource}
        onUpdateProjectOptions={null}
      />
      <QESButton
        qesType="none"
        qesTestId="project-action-menu__hide"
        onClick={() => handleHiddenChange(this.props.filteredProjectResource.resourceId, this.props.onHidden)}
        className="action-menu-item font-weight-normal col-12 d-block"
      >
        <i className="fa fa-low-vision" /> {this.props.showingHidden ? "Unhide" : "Hide"}
      </QESButton>
    </>
  )

  private onClickCancelProjectExecution = async (resourceId: number) => {
    const toastOptions: ToastOptions = {
      closeOnClick: true,
      containerId: "z-index-9999",
    };

    try {
      await handleCancelQueuedProject(resourceId);
      toast.info("Project execution canceled.", toastOptions);
    } catch {
      toast.error("An error occurred while attempting to cancel project execution.", toastOptions);
    }
  }
}
